import Split from 'react-split';
import { AritoIcon, AritoSplitPane, SidebarButton, ErpDataTableV2 } from '@/components/custom/erp';
import { GroupType, type Group, type GroupInput } from '@/types/schemas';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import { customerGroupColumns } from './cols-definition';
import type { CustomerGroupFormValues } from './schemas';
import { useGroup, useForms } from '@/hooks';
import { useData } from './hooks';

export const groupTypes = [
  {
    label: 'Nhóm công cụ 1 (CC1)',
    value: GroupType.TOOL1,
    shortCode: 'CC1'
  },
  {
    label: 'Nhóm công cụ 2 (CC2)',
    value: GroupType.TOOL2,
    shortCode: 'CC2'
  },
  {
    label: 'Nhóm công cụ 3 (CC3)',
    value: GroupType.TOOL3,
    shortCode: 'CC3'
  }
];

const DEFAULT_GROUP = groupTypes[0].value;

export default function NhomCCDCPage() {
  const {
    groups: groupsCC1,
    isLoading: isLoadingCC1,
    refreshGroups: refreshGroupsCC1,
    fetchGroupsByType: fetchGroupsByTypeCC1,
    addGroup: addGroupCC1,
    updateGroup: updateGroupCC1,
    deleteGroup: deleteGroupCC1
  } = useGroup(GroupType.TOOL1);

  const {
    groups: groupsCC2,
    isLoading: isLoadingCC2,
    refreshGroups: refreshGroupsCC2,
    fetchGroupsByType: fetchGroupsByTypeCC2,
    addGroup: addGroupCC2,
    updateGroup: updateGroupCC2,
    deleteGroup: deleteGroupCC2
  } = useGroup(GroupType.TOOL2);

  const {
    groups: groupsCC3,
    isLoading: isLoadingCC3,
    refreshGroups: refreshGroupsCC3,
    fetchGroupsByType: fetchGroupsByTypeCC3,
    addGroup: addGroupCC3,
    updateGroup: updateGroupCC3,
    deleteGroup: deleteGroupCC3
  } = useGroup(GroupType.TOOL3);

  const getGroupFunctions = (groupType: GroupType) => {
    switch (groupType) {
      case GroupType.TOOL1:
        return {
          groups: groupsCC1,
          refreshGroups: refreshGroupsCC1,
          fetchGroupsByType: fetchGroupsByTypeCC1,
          addGroup: addGroupCC1,
          updateGroup: updateGroupCC1,
          deleteGroup: deleteGroupCC1,
          isLoading: isLoadingCC1
        };
      case GroupType.TOOL2:
        return {
          groups: groupsCC2,
          refreshGroups: refreshGroupsCC2,
          fetchGroupsByType: fetchGroupsByTypeCC2,
          addGroup: addGroupCC2,
          updateGroup: updateGroupCC2,
          deleteGroup: deleteGroupCC2,
          isLoading: isLoadingCC2
        };
      case GroupType.TOOL3:
        return {
          groups: groupsCC3,
          refreshGroups: refreshGroupsCC3,
          fetchGroupsByType: fetchGroupsByTypeCC3,
          addGroup: addGroupCC3,
          updateGroup: updateGroupCC3,
          deleteGroup: deleteGroupCC3,
          isLoading: isLoadingCC3
        };
      default:
        return {
          groups: groupsCC1,
          refreshGroups: refreshGroupsCC1,
          fetchGroupsByType: fetchGroupsByTypeCC1,
          addGroup: addGroupCC1,
          updateGroup: updateGroupCC1,
          deleteGroup: deleteGroupCC1,
          isLoading: isLoadingCC1
        };
    }
  };

  const { activeGroup, handleFilter } = useData(DEFAULT_GROUP);

  const { isLoading, addGroup, updateGroup, deleteGroup, groups, refreshGroups } = getGroupFunctions(activeGroup);

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<Group>();

  const tables = [
    {
      name: '',
      rows: groups,
      columns: customerGroupColumns,
      key: `${activeGroup}-${Date.now()}`
    }
  ];

  const handleFormSubmit = async (data: CustomerGroupFormValues) => {
    try {
      const formData: GroupInput = {
        ...data,
        loai_nhom: activeGroup
      };

      if (formMode === 'add') {
        await addGroup(formData);
        await refreshGroups();
      } else if (formMode === 'edit' && selectedObj) {
        await updateGroup(selectedObj.uuid, formData);
        await refreshGroups();
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className='flex h-screen w-full min-w-0 max-w-full flex-row overflow-hidden bg-white'>
      <AritoSplitPane split='vertical' minSize={200} defaultSize={300}>
        <div className='h-full overflow-y-auto border-r'>
          <div className='p-4'>
            <h2 className='mb-4 flex items-center gap-1 text-lg font-semibold'>
              <AritoIcon icon={539} /> Danh mục phân nhóm
            </h2>
            <hr className='mb-4' />
            <ul>
              {groupTypes.map(group => (
                <li key={group.value} className='mb-2 text-sm'>
                  <SidebarButton isActive={activeGroup === group.value} onClick={() => handleFilter(group.value)}>
                    {group.label}
                  </SidebarButton>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className='h-full overflow-y-auto'>
          <div className='flex h-full flex-col'>
            <ActionBar
              onAddClick={handleAddClick}
              onEditClick={() => selectedObj && handleEditClick()}
              onDeleteClick={() => selectedObj && handleDeleteClick()}
              onCopyClick={() => selectedObj && handleCopyClick()}
              onViewClick={() => selectedObj && handleViewClick()}
              isViewDisabled={!selectedObj}
              activeGroup={activeGroup}
              onRefresh={() => refreshGroups()}
            />

            <div className='flex-1 overflow-hidden'>
              <Split
                className='flex min-w-0 flex-1 flex-col overflow-hidden'
                direction='vertical'
                sizes={[50, 50]}
                minSize={200}
                gutterSize={8}
                gutterAlign='center'
                snapOffset={30}
                dragInterval={1}
                cursor='row-resize'
              >
                <div className='w-full overflow-hidden'>
                  <ErpDataTableV2
                    key={activeGroup}
                    tables={tables}
                    onRowClick={handleRowClick}
                    selectedRowId={selectedRowIndex || undefined}
                    getRowId={row => row.uuid}
                    loading={isLoading}
                  />
                </div>
              </Split>
            </div>
          </div>
        </div>
      </AritoSplitPane>

      {showForm && (
        <FormDialog
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          formMode={formMode}
          onAddButtonClick={() => {
            handleCloseForm();

            clearSelection();

            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEditButtonClick={() => {
            if (selectedObj) {
              const objToEdit = { ...selectedObj };

              handleCloseForm();

              clearSelection();

              setTimeout(() => {
                handleRowClick({ id: objToEdit.uuid, row: objToEdit });

                handleEditClick();
              }, 100);
            }
          }}
          onDeleteButtonClick={() => {
            if (selectedObj) {
              const objToDelete = { ...selectedObj };

              handleCloseForm();

              clearSelection();

              setTimeout(() => {
                handleRowClick({ id: objToDelete.uuid, row: objToDelete });

                handleDeleteClick();
              }, 100);
            }
          }}
          onCopyButtonClick={() => {
            if (selectedObj) {
              const objToCopy = { ...selectedObj };

              handleCloseForm();

              clearSelection();

              setTimeout(() => {
                handleRowClick({ id: objToCopy.uuid, row: objToCopy });

                handleCopyClick();
              }, 100);
            }
          }}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? selectedObj
              : undefined
          }
          activeGroup={activeGroup}
          groups={groups}
          isCopyMode={isCopyMode}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          selectedObj={selectedObj}
          onClose={handleCloseDelete}
          deleteKhuVuc={deleteGroup}
          clearSelection={clearSelection}
        />
      )}
    </div>
  );
}
